import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { refApi } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Search } from "lucide-react";
import { ReferralThreadDrawer } from "./Partner";

export default function StaffReferralsPage() {
  const [refs, setRefs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | open | closed
  const [openThread, setOpenThread] = useState(null);

  const reload = () => refApi.mine().then(setRefs).catch(()=>toast.error("Could not load referrals"));
  useEffect(()=>{ reload(); }, []);

  const setOutcome = async (id, outcome) => {
    try {
      await refApi.outcome(id, { outcome, notes: "" });
      toast.success("Outcome recorded");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not record"); }
  };

  const filtered = refs.filter(r => {
    if (filter === "unread" && !(r.unread_messages > 0)) return false;
    if (filter === "open" && r.outcome) return false;
    if (filter === "closed" && !r.outcome) return false;
    if (q) {
      const hay = `${r.participant?.full_name || ""} ${r.summary || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const totalUnread = refs.reduce((sum, r) => sum + (r.unread_messages || 0), 0);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="staff-referrals-page">
        <div>
          <h1 className="font-serif text-3xl text-slate-900">Referrals Inbox</h1>
          <p className="text-sm text-slate-600 mt-1">
            All referrals you&apos;ve routed to partners and employers. Reply on any thread to keep the conversation going.
            {totalUnread > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-red-700 font-medium">
                <MessageSquare size={14}/> {totalUnread} unread message{totalUnread !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="ref-filter-row">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3 top-3 text-slate-400"/>
            <Input placeholder="Search by participant or summary" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="ref-search"/>
          </div>
          <div className="flex gap-1 items-center">
            {[
              { k: "all", label: "All" },
              { k: "unread", label: `Unread${totalUnread ? ` (${totalUnread})` : ""}` },
              { k: "open", label: "Open" },
              { k: "closed", label: "Closed" },
            ].map(opt => (
              <Button
                key={opt.k}
                size="sm"
                variant={filter === opt.k ? "default" : "outline"}
                onClick={() => setFilter(opt.k)}
                data-testid={`ref-filter-${opt.k}`}
                style={filter === opt.k ? { background: "var(--cod-navy)" } : {}}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500">No referrals match.</p>
          )}
          {filtered.map(r => (
            <Card key={r.id} data-testid={`staff-ref-${r.id}`}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="font-serif text-lg">
                      {r.participant?.full_name || "Participant"}
                    </CardTitle>
                    {r.participant_id && (
                      <Link
                        to={`/staff/participants/${r.participant_id}`}
                        className="text-xs text-slate-500 hover:underline"
                      >
                        Open participant record
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap items-start">
                    <Badge variant={r.priority === "P1" ? "destructive" : "outline"}>{r.priority}</Badge>
                    <Badge variant="secondary">{r.status}</Badge>
                    {r.outcome && <Badge>{r.outcome}</Badge>}
                    {r.unread_messages > 0 && (
                      <Badge className="text-white" style={{background:"var(--cod-red)"}} data-testid={`staff-unread-${r.id}`}>
                        {r.unread_messages} new
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700">{r.summary}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={()=>setOpenThread(r)} data-testid={`staff-msg-${r.id}`}>
                    <MessageSquare size={14} className="mr-1"/> Open thread
                  </Button>
                  {!r.outcome && (
                    <>
                      <Button size="sm" variant="outline" onClick={()=>setOutcome(r.id,"completed")} data-testid={`staff-complete-${r.id}`}>Mark complete</Button>
                      <Button size="sm" variant="ghost" onClick={()=>setOutcome(r.id,"declined")} data-testid={`staff-decline-${r.id}`}>Close as declined</Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {openThread && (
        <ReferralThreadDrawer
          referral={openThread}
          onClose={() => { setOpenThread(null); reload(); }}
        />
      )}
    </AppShell>
  );
}
